import { CircleCheckIcon, CircleXIcon, Loader2Icon, MinusIcon } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { LatestDataUpdate } from '@/lib/api/custom-dashboard';
import { isActiveDataUpdateStatus } from './dataUpdateJobStatus';
import type { CancelRequestTarget } from './dataUpdateTargets';
import { formatShortDateTime } from './formatShortDateTime';
import { InlineLinkButton, StatusLine, StatusNote } from './TabStatusParts';

export interface TabDataUpdateStatusProps {
  tabId: string;
  tabName: string;
  latestDataUpdate: LatestDataUpdate | null;
  /** キャンセル確認ダイアログを開くための要求。ダイアログ本体は呼び出し元（TabConfigScreen）が持つ。 */
  onRequestCancel: (target: CancelRequestTarget) => void;
  /** キャンセルAPI呼び出し中かどうか。DELETE解決前に行から再度キャンセルを要求できないよう無効化する。 */
  isCancelling: boolean;
  /** ジョブが無いときに添える短い説明（データ更新を始められない理由など）。 */
  idleHint?: string;
}

/**
 * タブ行の「データ更新」欄。直近ジョブの状態と日時、受付中・実行中はスピナー（実行中はキャンセル）、失敗時はエラー詳細を出す。
 * 定期バッチから起動したジョブ（T-0059）は「定期」と添える。
 */
export function TabDataUpdateStatus({
  tabId,
  tabName,
  latestDataUpdate,
  onRequestCancel,
  isCancelling,
  idleHint,
}: TabDataUpdateStatusProps) {
  if (latestDataUpdate === null) {
    return (
      <div>
        <StatusLine tone="neutral" icon={<MinusIcon aria-hidden />}>
          未実行
        </StatusLine>
        {idleHint && <StatusNote>{idleHint}</StatusNote>}
      </div>
    );
  }

  const { jobId, status, trigger, resultRevision, error, createdAt, finishedAt } = latestDataUpdate;
  const viaBatch = trigger === 'batch' ? '定期 · ' : '';

  if (status === 'queued') {
    // ジョブの行がまだ無いため、キャンセルはできない（行ができて「更新中」になってからキャンセルする）。
    const queuedAt = formatShortDateTime(createdAt);
    return (
      <div>
        <StatusLine
          tone="neutral"
          icon={<Loader2Icon aria-hidden className="animate-spin text-primary" />}
        >
          <span className="font-medium text-foreground">受付中</span>
        </StatusLine>
        {queuedAt && <StatusNote>{queuedAt} 受付</StatusNote>}
      </div>
    );
  }

  if (isActiveDataUpdateStatus(status)) {
    const startedAt = formatShortDateTime(createdAt);
    return (
      <div>
        <StatusLine
          tone="neutral"
          icon={<Loader2Icon aria-hidden className="animate-spin text-primary" />}
        >
          <span className="font-medium text-foreground">更新中</span>
        </StatusLine>
        <StatusNote>
          {viaBatch}
          {startedAt && `${startedAt} 開始 · `}
          <InlineLinkButton
            aria-label={`${tabName}のデータ更新をキャンセル`}
            onClick={() => onRequestCancel({ tabId, tabName, jobId })}
            disabled={isCancelling}
          >
            キャンセル
          </InlineLinkButton>
        </StatusNote>
      </div>
    );
  }

  const finished = formatShortDateTime(finishedAt);

  if (status === 'succeeded') {
    return (
      <div>
        <StatusLine tone="success" icon={<CircleCheckIcon aria-hidden />}>
          {resultRevision === null ? '完了' : `完了 → r${resultRevision}`}
        </StatusLine>
        {(viaBatch || finished) && (
          <StatusNote>
            {trigger === 'batch' ? ['定期', finished].filter(Boolean).join(' · ') : finished}
          </StatusNote>
        )}
      </div>
    );
  }

  const errorText = error ? `${error.code}: ${error.message}` : '不明なエラー';
  return (
    <div>
      <StatusLine tone="danger" icon={<CircleXIcon aria-hidden />} title={errorText}>
        失敗
      </StatusLine>
      <StatusNote>
        {viaBatch}
        {finished && `${finished} · `}
        <Popover>
          <PopoverTrigger asChild>
            <InlineLinkButton aria-label={`${tabName}のデータ更新のエラー詳細`}>
              エラー詳細
            </InlineLinkButton>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 space-y-1 text-sm">
            {error ? (
              <>
                <p className="font-mono text-xs text-muted-foreground">{error.code}</p>
                <p className="whitespace-pre-wrap break-words">{error.message}</p>
              </>
            ) : (
              <p>不明なエラー</p>
            )}
          </PopoverContent>
        </Popover>
      </StatusNote>
    </div>
  );
}
